import React, { useEffect, useRef, useState } from "react";
import { Canvas, IText, Image as FabricImage } from "fabric";

const FlyerEditor = ({ onExport, onClose }) => {

  const preset = { width: 600, height: 600 }; // Social media square size

  const [canvasSize, setCanvasSize] = useState(preset);
  const [selectedFontSize, setSelectedFontSize] = useState(24);
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [selectedFont, setSelectedFont] = useState("");
  const canvasRef = useRef(null);
  const canvasElRef = useRef(null);

  useEffect(() => {
    if (!canvasElRef.current) return;

    const fabricCanvas = new Canvas(canvasElRef.current, {
      width: canvasSize.width,
      height: canvasSize.height,
      backgroundColor,
      preserveObjectStacking: true,
    });

    canvasRef.current = fabricCanvas;

    const handleKeyDown = (e) => {
      const canvas = canvasRef.current;
      const activeObject = canvas?.getActiveObject();

      if (
        activeObject &&
        (e.key === "Delete" || e.key === "Backspace") &&
        !(activeObject.isEditing && activeObject.type === "i-text")
      ) {
        canvas.remove(activeObject);
        canvas.requestRenderAll();
        e.preventDefault();
      }
    };

    const updateTextControls = () => {
      const canvas = canvasRef.current;
      const obj = canvas.getActiveObject();
      if (obj && obj.type === "i-text") {
        setSelectedFontSize(obj.fontSize || 24);
        setSelectedColor(obj.fill || "#000000");
        setSelectedFont(obj.fontFamily || "Arial");
      }
    };

    const clearTextControls = () => {
      setSelectedFontSize(24);
      setSelectedColor("#000000");
      setSelectedFont("");
    };

    fabricCanvas.on("selection:created", updateTextControls);
    fabricCanvas.on("selection:updated", updateTextControls);
    fabricCanvas.on("selection:cleared", clearTextControls);

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      fabricCanvas.dispose();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [canvasSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.backgroundColor = backgroundColor;
      canvas.requestRenderAll();
    }
  }, [backgroundColor]);

  const addText = () => {
    const canvas = canvasRef.current;
    const text = new IText("Edit me", {
      left: 100,
      top: 100,
      fill: selectedColor,
      fontSize: selectedFontSize,
      fontFamily: selectedFont || "Arial",
      fontWeight: "normal",
      fontStyle: "normal",
      underline: false,
      textAlign: "left",
      editable: true,
      hasControls: true,
      lockScalingFlip: true,
    });
    canvas.add(text);
    canvas.setActiveObject(text);
    text.setControlsVisibility({
      mt: true, mb: true, ml: true, mr: true, bl: true, br: true, tl: true, tr: true,
      mtr: true
    });
    canvas.requestRenderAll();
  };

  const bringForward = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj) {
      canvas.bringForward(obj);
      canvas.requestRenderAll();
    }
  };

  const sendBackward = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj) {
      canvas.sendBackwards(obj);
      canvas.requestRenderAll();
    }
  };

  const bringToFront = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj) {
      canvas.bringToFront(obj);
      canvas.requestRenderAll();
    }
  };

  const sendToBack = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj) {
      canvas.sendToBack(obj);
      canvas.requestRenderAll();
    }
  };

  const toggleBold = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj && obj.type === "i-text") {
      obj.set("fontWeight", obj.fontWeight === "bold" ? "normal" : "bold");
      canvas.requestRenderAll();
    }
  };

  const toggleItalic = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj && obj.type === "i-text") {
      obj.set("fontStyle", obj.fontStyle === "italic" ? "normal" : "italic");
      canvas.requestRenderAll();
    }
  };

  const toggleUnderline = () => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj && obj.type === "i-text") {
      obj.set("underline", !obj.underline);
      canvas.requestRenderAll();
    }
  };

  const setTextAlign = (align) => {
    const canvas = canvasRef.current;
    const obj = canvas.getActiveObject();
    if (obj && obj.type === "i-text") {
      obj.set("textAlign", align);
      canvas.requestRenderAll();
    }
  };

  const addImage = (e) => {
    const canvas = canvasRef.current;
    const files = e.target.files;
    if (!files || !canvas) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const imgElement = new Image();
        imgElement.onload = () => {
          const imgWidth = imgElement.width;
          const imgHeight = imgElement.height;
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;

          const scale = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);

          const fabricImg = new FabricImage(imgElement, {
            scaleX: scale,
            scaleY: scale,
            left: (canvasWidth - imgWidth * scale) / 2,
            top: (canvasHeight - imgHeight * scale) / 2,
          });

          canvas.add(fabricImg);
          canvas.setActiveObject(fabricImg);
          canvas.requestRenderAll();
        };
        imgElement.onerror = (err) => console.error("❌ Image load error", err);
        imgElement.src = dataUrl;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

    const exportImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL({ format: "png" });

    if (onExport) onExport(dataUrl);

    if (onClose) {
        setTimeout(() => onClose(), 10);
    }
  };

  return (
    <div>
      <div className="toolbar">
        <div className="toolbar-group">
          <select
            value={selectedFont}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedFont(val);
              const obj = canvasRef.current.getActiveObject();
              if (obj && obj.type === "i-text") {
                obj.set("fontFamily", val);
                canvasRef.current.requestRenderAll();
              }
            }}
            aria-label="Font"
          >
            <option value="" disabled>-- Font --</option>
            <option value="Arial">Arial</option>
            <option value="Georgia">Georgia</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Verdana">Verdana</option>
            <option value="Courier New">Courier New</option>
          </select>
          <input
            type="number"
            value={selectedFontSize}
            onChange={(e) => {
              const size = parseInt(e.target.value);
              setSelectedFontSize(size);
              const obj = canvasRef.current.getActiveObject();
              if (obj && obj.type === "i-text") {
                obj.set("fontSize", size);
                canvasRef.current.requestRenderAll();
              }
            }}
            style={{ width: "60px" }}
            aria-label="Font Size"
          />
          <div className="color-picker">
            <input
              type="color"
              title="Text Color"
              value={selectedColor}
              onChange={(e) => {
                const color = e.target.value;
                setSelectedColor(color);
                const obj = canvasRef.current.getActiveObject();
                if (obj && obj.type === "i-text") {
                  obj.set("fill", color);
                  canvasRef.current.requestRenderAll();
                }
              }}
            />
          </div>
        </div>

        <div className="toolbar-group">
          <button type="button" title="Bold" onClick={toggleBold}><strong>B</strong></button>
          <button type="button" title="Italic" onClick={toggleItalic}><em>I</em></button>
          <button type="button" title="Underline" onClick={toggleUnderline}><u>U</u></button>
        </div>

        <div className="toolbar-group">
          <button type="button" title="Align Left" onClick={() => setTextAlign("left")}>⇤</button>
          <button type="button" title="Align Center" onClick={() => setTextAlign("center")}>⇥</button>
          <button type="button" title="Align Right" onClick={() => setTextAlign("right")}>⇸</button>
        </div>

        <div className="toolbar-group">
          <div className="color-picker">
            <input title="Background Color" type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)}  aria-label="Background Color" />
          </div>
        </div>

        <div className="toolbar-group">
          <button type="button" title="Bring Forward" onClick={bringForward}>⬆</button>
          <button type="button" title="Send Backward" onClick={sendBackward}>⬇</button>
          <button type="button" title="Bring To Front" onClick={bringToFront}>⬆⬆</button>
          <button type="button" title="Send To Back" onClick={sendToBack}>⬇⬇</button>
        </div>

        <div className="toolbar-group">
          <button type="button" title="Add Text" onClick={addText}>+ Text</button>
          <label className="btn" style={{ cursor: 'pointer', display: 'inline-block' }}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={addImage}
              style={{ display: 'none' }}
              title="You can upload multiple images."
            />
            + Images
          </label>
        </div>
      </div>

      <div
        style={{
          maxWidth: "100%",
          maxHeight: "600px",
          overflow: "auto",
          border: "1px solid #ccc",
        }}
      >
        <canvas ref={canvasElRef} style={{ border: 'dotted 1px #e0e0e0' }} />
      </div>
      <button type="button" title="Save" onClick={exportImage}>Save</button>
    </div>
  );
};

export default FlyerEditor;
